/**
 * LeadDashboard.js  —  "My Policy Access"
 * Mobile-first client portal: My Agent | My Policy | Upload Docs | Requests | Updates
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { registerPushNotifications, removePushListeners } from '../../utils/capacitorPush';
import { getIssuerLogo, getPlanBadge } from '../../utils/carrierAssets';

const API = process.env.REACT_APP_API_URL || '';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || '';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mypolicyaccess.app';
const APP_STORE_URL  = 'https://apps.apple.com/us/app/my-policy-access/id6804651324';
// Intent URL — real <a> link tap (NOT JS navigation) on Chrome for Android.
// Opens installed app via the mypolicyaccess:// scheme;
// S.browser_fallback_url goes to Play Store if the app is not installed.
const ANDROID_INTENT_URL = `intent://dashboard#Intent;scheme=mypolicyaccess;package=com.mypolicyaccess.app;S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
// Detect a real Android mobile/tablet browser (not desktop with DevTools emulation).
// navigator.userAgentData.platform is NOT spoofed by Chrome DevTools device mode,
// so "macOS" / "Windows" desktops stay false even when they simulate an Android UA.
const _detectAndroidWeb = () => {
  if (window.Capacitor?.isNativePlatform?.()) return false; // native app — no banner needed
  // Preferred: userAgentData (Chrome 90+, Edge, etc.)
  if (navigator.userAgentData) {
    const platform = (navigator.userAgentData.platform || '').toLowerCase();
    return platform === 'android';
  }
  // Fallback: classic UA string — exclude desktop OSes explicitly
  const ua = navigator.userAgent || '';
  return /android/i.test(ua) &&
    !/windows\s+nt|macintosh|mac\s+os|x86_64|wow64|cros/i.test(ua);
};
const isAndroidWeb = _detectAndroidWeb();

// Detect a real iOS mobile browser (Safari on iPhone/iPad, not in-app via Capacitor).
const _detectIosWeb = () => {
  if (window.Capacitor?.isNativePlatform?.()) return false; // already in the native app
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) && !/crios/i.test(ua);
};
const isIosWeb = _detectIosWeb();



/* ─── colour tokens ─────────────────────────────────────────────────────── */
const C = {
  brand:   '#1a56db',
  brandDk: '#1344b0',
  success: '#0e9f6e',
  warn:    '#e3a008',
  danger:  '#e02424',
  bg:      '#f9fafb',
  card:    '#ffffff',
  border:  '#e5e7eb',
  text:    '#111827',
  muted:   '#6b7280',
  light:   '#f3f4f6',
};

/* ─── helpers ───────────────────────────────────────────────────────────── */

/**
 * fileViewUrl – convert a stored S3 URL into the server-proxy download URL.
 * Embeds the JWT as a query param so <img src> and <a target="_blank"> work
 * (browsers don't send the Authorization header for those requests).
 * The server validates the token and issues a 302 → presigned S3 GET URL.
 */
function fileViewUrl(fileUrl) {
  if (!fileUrl) return '#';
  const match = fileUrl.match(/lead-portal\/.+/);
  if (!match) return fileUrl;
  const tok = localStorage.getItem('leadToken') || '';
  return `${API}/api/lead-portal/files/download?key=${encodeURIComponent(match[0])}&token=${encodeURIComponent(tok)}`;
}

const fmt = (v) => (v == null || v === '' ? '—' : v);
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return v; }
};
const fmtCurrency = (v) => {
  if (!v) return '—';
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? v : `$${n.toFixed(2)}/mo`;
};
const AUTH_HEADER = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('leadToken')}` } });

// GHL sometimes stores content as an object {type, body} — normalize to string
function safeContent(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'object') {
    return content.body || content.text || content.message || content.preview || JSON.stringify(content);
  }
  return String(content);
}

/* ─── nav items ─────────────────────────────────────────────────────────── */
const NAV = [
  { id: 'agent',    label: 'My Agent',  icon: '👤' },
  { id: 'coverage', label: 'My Policy', icon: '🛡️' },
  { id: 'docs',     label: 'Docs',      icon: '📎' },
  { id: 'benefits', label: 'Benefits',  icon: '🎁' },
  { id: 'requests', label: 'Requests',  icon: '📋' },
];

/* ─── benefit items ──────────────────────────────────────────────────────── */
const BENEFIT_ITEMS = [
  { id: 'dental',      label: 'Dental',          desc: 'Cleanings, exams, fillings, dentures & more.',        bg: '#22c55e', icon: '🦷' },
  { id: 'vision',      label: 'Vision',           desc: 'Eye exams, glasses, contacts & more.',                bg: '#3b82f6', icon: '👁️' },
  { id: 'hearing',     label: 'Hearing',          desc: 'Hearing exams, devices & fitting support.',           bg: '#8b5cf6', icon: '👂' },
  { id: 'otc',         label: 'Over-the-Counter', desc: 'OTC items, vitamins, health essentials.',             bg: '#f97316', icon: '🧺' },
  { id: 'health_card', label: 'Health Card',      desc: 'Use your card for approved expenses and savings.',    bg: '#0d9488', icon: '💳' },
  { id: 'wellness',    label: 'Wellness',          desc: 'Fitness, mental health, wellness programs.',         bg: '#ec4899', icon: '❤️' },
  { id: 'more',        label: 'More Benefits',    desc: 'Transportation, meals, chiropractic & more.',         bg: '#f59e0b', icon: '⭐' },
];

const REQUEST_TYPES = [
  { id: 'call_me',              label: '📞 Call Me' },
  { id: 'help_with_card',       label: '💳 I Need Help With My Card' },
  { id: 'update_my_info',       label: '✏️ I Need to Update My Info' },
  { id: 'received_notice',      label: '📬 I Received a Notice' },
  { id: 'upload_documents',     label: '📎 I Need to Upload Documents' },
  { id: 'review_plan',          label: '🔍 I Want to Review My Plan' },
  { id: 'policy_status_changed',label: '⚠️ My Policy Status Changed' },
  { id: 'benefit_request',      label: '🎁 Benefit Request' },
  { id: 'other',                label: '💬 Other' },
];

const DOC_TYPES = [
  'Government-issued ID',
  'Proof of Income',
  'Medicare Card',
  'Medicaid Card',
  'Insurance Card',
  'Other Document',
];

/* ─── styles ─────────────────────────────────────────────────────────────── */
const s = {
  root: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: C.bg,
    minHeight: '100dvh',
    maxWidth: 480,
    margin: '0 auto',
    position: 'relative',
    /* iOS home-indicator + tab bar height. env() = 0 on web — no impact. */
    paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
  },
  header: {
    background: C.brand,
    color: '#fff',
    /* Accounts for iOS notch / Dynamic Island. Harmless on web (= 14px). */
    paddingTop: 'max(14px, env(safe-area-inset-top, 14px))',
    paddingBottom: 12,
    paddingLeft: 16,
    paddingRight: 16,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: { fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px' },
  headerSub:   { fontSize: 12, opacity: 0.8, marginTop: 2 },
  navBar: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    background: '#fff',
    borderTop: `1px solid ${C.border}`,
    display: 'flex',
    zIndex: 100,
    /* iOS home-indicator safe area. env() = 0 on web — no impact. */
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  navItem: (active) => ({
    flex: 1,
    padding: '8px 4px 6px',
    textAlign: 'center',
    cursor: 'pointer',
    borderTop: active ? `2px solid ${C.brand}` : '2px solid transparent',
    background: active ? '#eff6ff' : 'transparent',
  }),
  navIcon:  { fontSize: 18, display: 'block' },
  navLabel: (active) => ({
    fontSize: 10,
    color: active ? C.brand : C.muted,
    fontWeight: active ? 700 : 400,
    display: 'block',
    marginTop: 2,
  }),
  section: { padding: '12px 14px' },
  card: {
    background: C.card,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    padding: 16,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: `1px solid ${C.light}`,
  },
  rowLabel: { fontSize: 13, color: C.muted },
  rowValue: { fontSize: 13, color: C.text, fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' },
  btnPrimary: {
    background: C.brand,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    marginTop: 8,
  },
  btnOutline: {
    background: 'transparent',
    color: C.brand,
    border: `1.5px solid ${C.brand}`,
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    flex: 1,
    marginRight: 6,
  },
  btnDanger: {
    background: 'transparent',
    color: C.danger,
    border: `1.5px solid ${C.danger}`,
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    flex: 1,
  },
  agentName:   { fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 2 },
  agentEmail:  { fontSize: 13, color: C.muted,  marginBottom: 10 },
  actionRow:   { display: 'flex', gap: 8, marginTop: 12 },
  actionBtn: (color) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: color + '15',
    border: `1.5px solid ${color}`,
    borderRadius: 10,
    padding: '10px 4px',
    cursor: 'pointer',
    gap: 4,
  }),
  actionBtnIcon:  { fontSize: 22 },
  actionBtnLabel: (color) => ({ fontSize: 11, fontWeight: 600, color }),
  badge: (color) => ({
    display: 'inline-block',
    background: color + '20',
    color,
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
  }),
  chatWrap: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100dvh - 130px)',
  },
  msgList: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  msgBubble: (mine) => ({
    maxWidth: '78%',
    alignSelf: mine ? 'flex-end' : 'flex-start',
    background: mine ? C.brand : '#fff',
    color: mine ? '#fff' : C.text,
    borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    padding: '9px 13px',
    fontSize: 14,
    lineHeight: 1.4,
    boxShadow: '0 1px 3px rgba(0,0,0,.08)',
  }),
  msgMeta: (mine) => ({ fontSize: 10, color: mine ? 'rgba(255,255,255,.65)' : C.muted, marginTop: 3, textAlign: mine ? 'right' : 'left' }),
  chatInput: {
    display: 'flex',
    gap: 8,
    padding: '10px 14px',
    background: '#fff',
    borderTop: `1px solid ${C.border}`,
  },
  textInput: {
    flex: 1,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: '9px 14px',
    fontSize: 14,
    outline: 'none',
    background: C.light,
  },
  sendBtn: {
    background: C.brand,
    color: '#fff',
    border: 'none',
    borderRadius: 20,
    padding: '9px 16px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
  },
  tabBar: {
    display: 'flex',
    borderBottom: `1px solid ${C.border}`,
    background: '#fff',
    overflowX: 'auto',
  },
  tab: (active) => ({
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: active ? 700 : 400,
    color: active ? C.brand : C.muted,
    borderBottom: active ? `2px solid ${C.brand}` : '2px solid transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
  }),
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    color: C.muted,
    fontSize: 14,
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px 20px',
    color: C.muted,
  },
  emptyIcon: { fontSize: 40, display: 'block', marginBottom: 10 },
  toast: (show) => ({
    position: 'fixed',
    bottom: 80,
    left: '50%',
    transform: `translateX(-50%) translateY(${show ? 0 : 16}px)`,
    opacity: show ? 1 : 0,
    transition: 'all .25s ease',
    background: C.text,
    color: '#fff',
    borderRadius: 8,
    padding: '9px 18px',
    fontSize: 13,
    zIndex: 200,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  }),
  fileDropZone: {
    border: `2px dashed ${C.border}`,
    borderRadius: 12,
    padding: '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    background: C.light,
    marginBottom: 12,
  },
  statusBadge: (status) => {
    const s = (status || '').toLowerCase();
    const color = s.includes('active') || s.includes('enroll') ? C.success
                : s.includes('pend') ? C.warn
                : s.includes('cancel') || s.includes('term') ? C.danger
                : C.brand;
    return { display: 'inline-block', background: color + '18', color, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 };
  },
};

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function LeadDashboard({ onLogout, onGoToOnboarding }) {
  const [tab, setTab]               = useState('agent');
  const [profile, setProfile]       = useState(null);
  const [coverages, setCoverages]   = useState([]);
  const [activeIns, setActiveIns]   = useState(0);
  const [requests, setRequests]     = useState([]);
  const [updates, setUpdates]       = useState([]);
  const [messages, setMessages]     = useState([]);
  const [loading, setLoading]       = useState({});
  const [chatInput, setChatInput]   = useState('');
  const [toastMsg, setToastMsg]     = useState('');
  const [showToast, setShowToast]   = useState(false);
  const [reqForm, setReqForm]       = useState({ type: '', note: '', show: false });
  const [chatOpen, setChatOpen]       = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [uploading, setUploading]     = useState(false);
  const [docType, setDocType]       = useState(DOC_TYPES[0]);
  const [benefitDetail, setBenefitDetail] = useState(null);
  const [benefitNote, setBenefitNote]     = useState('');
  const [updatesOpen, setUpdatesOpen]     = useState(false);
  const [profileOpen, setProfileOpen]         = useState(false);
  const [avatarDropdown, setAvatarDropdown]   = useState(false);
  const [showAndroidAppModal, setShowAndroidAppModal] = useState(false);
  const [showIosAppModal,     setShowIosAppModal]     = useState(false);
  const [ghlProfile, setGhlProfile]           = useState(null);
  const [loadingGhlProfile, setLoadingGhlProfile] = useState(false);
  const [showMarketingBanner, setShowMarketingBanner] = useState(false);
  const [marketingCountdown, setMarketingCountdown]   = useState(5);
  const msgEndRef     = useRef(null);
  const fileRef       = useRef(null);
  const socketRef     = useRef(null);
  const pollTimerRef  = useRef(null);
  const pollCountRef  = useRef(0);
  const chatOpenRef   = useRef(false);

  /* ── toast helper ── */
  const toast = useCallback((msg) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2800);
  }, []);

  const setLoad = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  /* ── Android app download modal — show once per session on Android web ──
     Key is versioned (_v3) so bumping the version resets it for all users
     who previously dismissed the old version. */
  useEffect(() => {
    if (!isAndroidWeb) return;
    const dismissed = localStorage.getItem('androidAppBannerDismissed_v3');
    if (!dismissed) setShowAndroidAppModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissAndroidModal = () => {
    localStorage.setItem('androidAppBannerDismissed_v3', '1');
    setShowAndroidAppModal(false);
  };

  /* ── iOS App Store modal — show once per session on iOS Safari ── */
  useEffect(() => {
    if (!isIosWeb) return;
    const dismissed = localStorage.getItem('iosAppBannerDismissed_v1');
    if (!dismissed) setShowIosAppModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissIosModal = () => {
    localStorage.setItem('iosAppBannerDismissed_v1', '1');
    setShowIosAppModal(false);
  };

  /* ── Marketing URL compliance banner ──────────────────────────────────────
     Show agent's marketing URL once (1-year cooldown) after coverages load.
     The banner has a 5-second countdown then auto-opens the link.           */
  useEffect(() => {
    if (!coverages.length) return;
    const ins = coverages[0];
    if (!ins?.agentMarketingUrl) return;

    const leadId = profile?.leadEmail || profile?.email || 'lead';
    const STORAGE_KEY = `marketingUrlShown_${leadId}_${ins.agentEmail || 'agent'}`;
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown) {
      const diffMs = Date.now() - parseInt(lastShown, 10);
      const oneYear = 365 * 24 * 60 * 60 * 1000;
      if (diffMs < oneYear) return; // already shown within 1 year
    }
    setMarketingCountdown(5);
    setShowMarketingBanner(true);
  }, [coverages]);

  useEffect(() => {
    if (!showMarketingBanner) return;
    if (marketingCountdown <= 0) {
      // Auto-open the marketing URL
      const ins = coverages[0];
      if (ins?.agentMarketingUrl) {
        const leadId = profile?.leadEmail || profile?.email || 'lead';
        const STORAGE_KEY = `marketingUrlShown_${leadId}_${ins.agentEmail || 'agent'}`;
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        if (window.Capacitor?.isNativePlatform?.()) {
          import('@capacitor/browser').then(({ Browser }) => {
            Browser.open({ url: ins.agentMarketingUrl });
          }).catch(() => { window.open(ins.agentMarketingUrl, '_blank'); });
        } else {
          window.open(ins.agentMarketingUrl, '_blank');
        }
      }
      setShowMarketingBanner(false);
      return;
    }
    const timer = setTimeout(() => setMarketingCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMarketingBanner, marketingCountdown]);

  const openMarketingUrl = () => {
    const ins = coverages[0];
    if (!ins?.agentMarketingUrl) return;
    const leadId = profile?.leadEmail || profile?.email || 'lead';
    const STORAGE_KEY = `marketingUrlShown_${leadId}_${ins.agentEmail || 'agent'}`;
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowMarketingBanner(false);
    if (window.Capacitor?.isNativePlatform?.()) {
      import('@capacitor/browser').then(({ Browser }) => {
        Browser.open({ url: ins.agentMarketingUrl });
      }).catch(() => { window.open(ins.agentMarketingUrl, '_blank'); });
    } else {
      window.open(ins.agentMarketingUrl, '_blank');
    }
  };

  /* ── keep chatOpenRef in sync so push callback can read current value ── */
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);

  /* ── register push notifications (Capacitor native only — no-op on web) ── */
  useEffect(() => {
    const leadToken = localStorage.getItem('leadToken');
    if (!leadToken) return;
    registerPushNotifications(leadToken, API, ({ title, body }) => {
      toast(`${title}: ${body}`);
      if (!chatOpenRef.current) { setChatUnreadCount(p => p + 1); }
    });
    return () => { removePushListeners(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── fetch profile ── */
  useEffect(() => {
    setLoad('profile', true);
    axios.get(`${API}/api/lead-portal/lead/profile`, AUTH_HEADER())
      .then(r => {
        setProfile(r.data.account);
        const cvgs = r.data.coverages || [];
        setCoverages(cvgs);
        // ⚠️ Only redirect to onboarding when the SERVER says onboarding is not complete.
        // Do NOT redirect based on coverages being empty — an enrolled lead may have
        // coverages:[] briefly (race condition after "Continue" click) or coverage:null
        // (plan details not yet filled in GHL).  onboardingComplete is the authoritative flag.
        if (!r.data.account?.onboardingComplete && onGoToOnboarding) {
          onGoToOnboarding();
        }
      })
      .catch(() => toast('Failed to load profile'))
      .finally(() => setLoad('profile', false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── auto-poll for agent assignment when coverages empty after onboarding ── */
  // The /track fire-and-forget block writes insurances AFTER the 200 response,
  // so a newly-onboarded lead may see coverages:[] for 30–120 seconds.
  // Poll every 10 seconds (max 30 × 10s = 5 min) until coverages appear.
  useEffect(() => {
    // Only start polling if onboarding is complete but no coverages yet
    if (!profile?.onboardingComplete || coverages.length > 0) return;

    const MAX_POLLS = 30;
    pollCountRef.current = 0;

    const poll = () => {
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollTimerRef.current);
        return;
      }
      pollCountRef.current += 1;
      axios.get(`${API}/api/lead-portal/lead/profile`, AUTH_HEADER())
        .then(r => {
          const cvgs = r.data.coverages || [];
          if (cvgs.length > 0) {
            clearInterval(pollTimerRef.current);
            setProfile(r.data.account);
            setCoverages(cvgs);
          }
        })
        .catch(() => {}); // silent — keep polling
    };

    pollTimerRef.current = setInterval(poll, 10000);
    return () => clearInterval(pollTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.onboardingComplete, coverages.length]);

  /* ── fetch requests ── */
  useEffect(() => {
    if (tab !== 'requests') return;
    setLoad('requests', true);
    axios.get(`${API}/api/lead-portal/lead/requests`, AUTH_HEADER())
      .then(r => setRequests(r.data.requests || []))
      .catch(() => {})
      .finally(() => setLoad('requests', false));
  }, [tab]);

  /* ── fetch updates (on mount — shown via bell icon, not a tab) ── */
  useEffect(() => {
    setLoad('updates', true);
    axios.get(`${API}/api/lead-portal/lead/updates`, AUTH_HEADER())
      .then(r => setUpdates(r.data.updates || []))
      .catch(() => {})
      .finally(() => setLoad('updates', false));
  }, []);

  /* ── fetch GHL profile when My Account panel is opened ── */
  useEffect(() => {
    if (!profileOpen) return;
    setLoadingGhlProfile(true);
    axios.get(`${API}/api/lead-portal/lead/ghl-profile`, AUTH_HEADER())
      .then(r => setGhlProfile(r.data))
      .catch(() => {})
      .finally(() => setLoadingGhlProfile(false));
  }, [profileOpen]);

  /* ── poll server badge counter every 30 s ── */
  const activeConvId = coverages[activeIns]?.conversationId;
  useEffect(() => {
    const fetchBadge = () => {
      if (chatOpenRef.current) return;
      axios.get(`${API}/api/lead-auth/badge-count`, AUTH_HEADER())
        .then(r => setChatUnreadCount(r.data.count || 0))
        .catch(() => {});
    };
    fetchBadge();
    const id = setInterval(fetchBadge, 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── clear badge + reset server counter when chat opens ── */
  useEffect(() => {
    if (!chatOpen || !activeConvId) return;
    setChatUnreadCount(0);
    axios.post(`${API}/api/lead-auth/clear-badge`, {}, AUTH_HEADER()).catch(() => {});
  }, [chatOpen, activeConvId]);

  /* ── fetch messages & setup socket ── */
  useEffect(() => {
    if (!chatOpen || !activeConvId) return;
    setLoad('messages', true);
    axios.get(`${API}/api/lead-portal/conversations/${activeConvId}/messages?limit=50`, AUTH_HEADER())
      .then(r => setMessages((r.data.messages || []).reverse()))
      .catch(() => {})
      .finally(() => setLoad('messages', false));

    // ── Connect to the /chat namespace (must match server's io.of('/chat')) ──
    const sock = io(`${SOCKET_URL || ''}/chat`, {
      auth: { token: localStorage.getItem('leadToken') },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      // Join the conversation room once connected
      // Server listens for 'join:conversation' with { conversationId }
      sock.emit('join:conversation', { conversationId: activeConvId });
    });

    // Server emits 'message:new' with { message } wrapper
    sock.on('message:new', ({ message }) => {
      setMessages(p => {
        // Deduplicate by messageId (in case the sender already added it optimistically)
        if (p.find(m => m.messageId === message.messageId)) return p;
        return [...p, message];
      });
    });

    sock.on('message:deleted', ({ messageId }) => {
      setMessages(p => p.map(m => m.messageId === messageId ? { ...m, deleted: true, content: 'Message deleted' } : m));
    });

    sock.on('message:reaction', ({ messageId, reactions }) => {
      setMessages(p => p.map(m => m.messageId === messageId ? { ...m, reactions } : m));
    });

    sock.on('connect_error', (err) => {
      console.warn('[CHAT] Socket connection error:', err.message);
    });

    return () => {
      sock.emit('leave:conversation', { conversationId: activeConvId });
      sock.disconnect();
      socketRef.current = null;
    };
  }, [chatOpen, activeConvId]);

  /* ── scroll to bottom ── */
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  /* ── send chat message ── */
  const sendMsg = async () => {
    const text = chatInput.trim();
    if (!text || !activeConvId) return;
    setChatInput('');
    try {
      const res = await axios.post(
        `${API}/api/lead-portal/conversations/${activeConvId}/messages`,
        { content: text, type: 'text' }, AUTH_HEADER()
      );
      // Optimistically add the message immediately from API response.
      // The socket will also fire 'message:new' — dedup by messageId prevents doubles.
      const newMsg = res?.data?.message;
      if (newMsg) {
        setMessages(p => p.find(m => m.messageId === newMsg.messageId) ? p : [...p, newMsg]);
      }
    } catch (err) {
      const msg = err?.response?.data?.error;
      if (msg && err?.response?.data?.moderated) {
        toast(msg, { autoClose: 6000 });
        // Restore input so user can edit
        setChatInput(text);
      } else {
        toast('Failed to send');
      }
    }
  };

  /* ── upload doc (server-proxy — no browser→S3, avoids CORS) ── */
  const uploadDoc = async (file) => {
    if (!file || !activeConvId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      // Rename file to include docType prefix
      const renamedFile = new File([file], `[${docType}] ${file.name}`, { type: file.type });
      fd.append('file', renamedFile);
      fd.append('caption', `Document: ${docType}`);

      const { data } = await axios.post(
        `${API}/api/lead-portal/conversations/${activeConvId}/upload`,
        fd,
        { headers: { ...AUTH_HEADER().headers } } // axios sets Content-Type multipart automatically
      );
      // Add message to local state so it shows immediately without waiting for socket
      if (data.message) {
        setMessages(p => p.find(m => m.messageId === data.message.messageId) ? p : [...p, data.message]);
      }
      toast('✅ Document uploaded');
    } catch { toast('Upload failed. Please try again.'); }
    finally { setUploading(false); }
  };

  /* ── submit benefit request ── */
  const submitBenefitRequest = async () => {
    if (!benefitDetail) return;
    try {
      await axios.post(`${API}/api/lead-portal/lead/requests`,
        {
          type: 'benefit_request',
          note: benefitNote
            ? `${benefitDetail.label}: ${benefitNote}`
            : `Request: ${benefitDetail.label}`,
          insuranceType: coverages[activeIns]?.insuranceType || 'aca',
        },
        AUTH_HEADER()
      );
      toast(`✅ ${benefitDetail.label} request sent!`);
      setBenefitDetail(null);
      setBenefitNote('');
    } catch { toast('Failed to submit request'); }
  };

  /* ── submit request ── */
  const submitRequest = async () => {
    if (!reqForm.type) { toast('Please select a request type'); return; }
    try {
      await axios.post(`${API}/api/lead-portal/lead/requests`,
        { type: reqForm.type, note: reqForm.note, insuranceType: coverages[activeIns]?.insuranceType || 'aca' },
        AUTH_HEADER()
      );
      toast('✅ Request submitted!');
      setReqForm({ type: '', note: '', show: false });
      // refresh
      const r = await axios.get(`${API}/api/lead-portal/lead/requests`, AUTH_HEADER());
      setRequests(r.data.requests || []);
    } catch { toast('Failed to submit request'); }
  };

  /* ── logout ── */
  const handleLogout = () => {
    localStorage.removeItem('leadToken');
    if (onLogout) onLogout();
    else window.location.href = '/lead/login';
  };

  /* ── active coverage ── */
  const cov = coverages[activeIns] || {};

  /* ═══════════ SECTION: My Agent (Home Dashboard) ═══════════ */
  const SectionAgent = () => {
    if (loading.profile) return <div style={s.spinner}>Loading…</div>;

    if (!coverages.length) return (
      <div style={s.section}>
        <div style={s.emptyState}>
          <span style={s.emptyIcon}>👤</span>
          <p style={{ fontWeight: 700, marginBottom: 6 }}>Agent Assignment Pending</p>
          <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>We're connecting you with a licensed agent.</p>
        </div>
      </div>
    );

    const ins        = coverages[activeIns];
    const agentName  = ins?.agentName  || 'Your Agent';
    const agentPhone = ins?.agentPhone || '';
    const agentEmail = ins?.agentEmail || '';
    const coverage   = ins?.coverage   || {};

    return (
      <div style={s.section}>
        {/* ── MY AGENT CARD ── */}
        <div style={s.card}>
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>My Agent</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.brand + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
              {ins.agentAvatar
                ? <img src={ins.agentAvatar} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                : '👤'}
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 2 }}>{agentName}</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>Licensed Insurance Agent</div>
              <div style={{ fontSize: 12, color: C.muted }}>{(ins.insuranceType || '').toUpperCase()} Specialist</div>
            </div>
          </div>
          {/* Bordered action buttons — Call / Text / Email / Chat */}
          <div style={{ display: 'flex', borderTop: `1px solid ${C.light}`, paddingTop: 12, gap: 8 }}>
            {agentPhone && (
              <a href={`tel:${agentPhone}`} style={{ flex: 1, textDecoration: 'none' }}>
                <div style={{ textAlign: 'center', padding: '10px 4px', border: `1.5px solid ${C.success}`, borderRadius: 10, background: C.success + '12' }}>
                  <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>📞</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.success }}>Call</span>
                </div>
              </a>
            )}
            {agentPhone && (
              <a href={`sms:${agentPhone}`} style={{ flex: 1, textDecoration: 'none' }}>
                <div style={{ textAlign: 'center', padding: '10px 4px', border: `1.5px solid ${C.brand}`, borderRadius: 10, background: C.brand + '12' }}>
                  <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>💬</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.brand }}>Text</span>
                </div>
              </a>
            )}
            {agentEmail && (
              <a href={`mailto:${agentEmail}`} style={{ flex: 1, textDecoration: 'none' }}>
                <div style={{ textAlign: 'center', padding: '10px 4px', border: `1.5px solid ${C.warn}`, borderRadius: 10, background: C.warn + '12' }}>
                  <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>✉️</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.warn }}>Email</span>
                </div>
              </a>
            )}
            <div style={{ flex: 1, cursor: 'pointer', position: 'relative' }} onClick={() => setChatOpen(true)}>
              {/* pulse ring when unread */}
              {chatUnreadCount > 0 && (
                <style>{`
                  @keyframes badgePulse {
                    0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); }
                    70%  { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
                    100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
                  }
                  .chat-badge-dot { animation: badgePulse 2s ease-out infinite; }
                `}</style>
              )}
              <div style={{
                textAlign: 'center', padding: '10px 4px',
                border: `1.5px solid ${chatUnreadCount > 0 ? '#ef4444' : C.brandDk}`,
                borderRadius: 10,
                background: chatUnreadCount > 0
                  ? 'rgba(239,68,68,0.07)'
                  : C.brandDk + '12',
                transition: 'border-color 0.2s, background 0.2s',
              }}>
                <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>🗨️</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: chatUnreadCount > 0 ? '#ef4444' : C.brandDk }}>Chat</span>
              </div>
              {chatUnreadCount > 0 && (
                <div
                  className="chat-badge-dot"
                  style={{
                    position: 'absolute', top: -9, right: -9,
                    minWidth: 20, height: 20,
                    background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
                    borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: chatUnreadCount > 9 ? 9 : 11,
                    fontWeight: 800, color: '#fff',
                    padding: '0 5px',
                    border: '2.5px solid #fff',
                    lineHeight: 1, pointerEvents: 'none', zIndex: 10,
                    letterSpacing: '-0.3px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── MARKETING URL COMPLIANCE BANNER ── */}
        {showMarketingBanner && ins.agentMarketingUrl && (
          <div style={{
            ...s.card,
            background: 'linear-gradient(135deg, #1a56db 0%, #1e40af 100%)',
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          }}>
            <button
              onClick={openMarketingUrl}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.6)',
                borderRadius: 10, padding: '10px 14px', color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <span style={{ fontSize: 20 }}>🔗</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Your Agent's Portal</div>
                <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>Tap to open in browser</div>
              </div>
            </button>
            {/* Countdown circle */}
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <svg width="48" height="48" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
                <circle
                  cx="24" cy="24" r="20" fill="none"
                  stroke="url(#mktGrad)" strokeWidth="4"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (marketingCountdown / 5)}`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.9s linear' }}
                />
                <defs>
                  <linearGradient id="mktGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#60a5fa"/>
                    <stop offset="100%" stopColor="#a5f3fc"/>
                  </linearGradient>
                </defs>
              </svg>
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff',
              }}>{marketingCountdown}</div>
            </div>
          </div>
        )}

        {/* ── COVERAGE OVERVIEW ── */}
        {(() => {
          const issuerLogo = getIssuerLogo(coverage.carrierName);
          const planBadge  = getPlanBadge(coverage.planName);
          return (
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Coverage Overview</span>
                <span style={s.badge(C.success)}>Active</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                {/* Left icon — plan badge at native 24×24 centered, or shield emoji */}
                <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: 10, background: C.brand + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {planBadge
                    ? <img src={planBadge} alt="" style={{ width: 24, height: 24 }} />
                    : <span style={{ fontSize: 24 }}>🛡️</span>}
                </div>
                {/* Plan name + member info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {coverage.carrierName && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {coverage.carrierName}
                    </div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2 }}>
                    {coverage.planName || (ins.insuranceType || 'ACA').toUpperCase() + ' Plan'}
                  </div>
                  {coverage.memberId && <div style={{ fontSize: 12, color: C.muted }}>Plan ID: {coverage.memberId}</div>}
                  {coverage.effectiveDate && <div style={{ fontSize: 12, color: C.muted }}>Member since {fmtDate(coverage.effectiveDate)}</div>}
                </div>
              </div>
              {/* Bottom row: View Plan Details on left, carrier logo on right */}
              <div style={{ borderTop: `1px solid ${C.light}`, paddingTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span
                  style={{ fontSize: 13, color: C.brand, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                  onClick={() => setTab('coverage')}
                >View Plan Details ›</span>
                {issuerLogo && (
                  <img
                    src={issuerLogo}
                    alt={coverage.carrierName}
                    style={{ maxHeight: 32, maxWidth: '55%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                  />
                )}
              </div>
            </div>
          );
        })()}

        {/* ── BENEFITS & SAVINGS ── */}
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Benefits & Savings</span>
            <span style={{ fontSize: 13, color: C.brand, fontWeight: 600, cursor: 'pointer' }} onClick={() => setTab('benefits')}>View All</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: C.light, borderRadius: 10, padding: 12 }}>
              <span style={{ fontSize: 22, display: 'block', marginBottom: 6 }}>💊</span>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>Rx Savings</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>Save on prescriptions near you.</div>
              <span style={{ fontSize: 12, color: C.brand, fontWeight: 600, cursor: 'pointer' }} onClick={() => setTab('benefits')}>Find Savings ›</span>
            </div>
            <div style={{ background: C.light, borderRadius: 10, padding: 12 }}>
              <span style={{ fontSize: 22, display: 'block', marginBottom: 6 }}>💳</span>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>Health Card</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>View your digital health card.</div>
              <span style={{ fontSize: 12, color: C.brand, fontWeight: 600, cursor: 'pointer' }} onClick={() => setTab('benefits')}>View Card ›</span>
            </div>
          </div>
        </div>

        {/* ── RECENT UPDATES ── */}
        {updates.length > 0 && (
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Recent Updates</span>
              <span style={{ fontSize: 13, color: C.brand, fontWeight: 600, cursor: 'pointer' }} onClick={() => setUpdatesOpen(true)}>View All</span>
            </div>
            {updates.slice(0, 3).map((u, i) => (
              <div
                key={u.updateId}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderTop: `1px solid ${C.light}`, cursor: 'pointer' }}
                onClick={() => setUpdatesOpen(true)}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: u.readAt ? C.muted : C.brand, flexShrink: 0, marginTop: 4, display: 'inline-block' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{u.title}</div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.body}</div>
                </div>
                <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginTop: 2 }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ═══════════ SECTION: My Policy ═══════════ */
  const SectionCoverage = () => {
    if (loading.profile) return <div style={s.spinner}>Loading…</div>;
    if (!coverages.length) return (
      <div style={s.emptyState}>
        <span style={s.emptyIcon}>🛡️</span>
        <p>No coverage on file yet.</p>
      </div>
    );

    const ins = coverages[activeIns];
    const cov = ins?.coverage || {};

    return (
      <div style={s.section}>
        {coverages.length > 1 && (
          <div style={s.tabBar}>
            {coverages.map((c, i) => (
              <div key={c.insuranceType} style={s.tab(i === activeIns)} onClick={() => setActiveIns(i)}>
                {c.insuranceType.toUpperCase()}
              </div>
            ))}
          </div>
        )}

        {/* Status Card */}
        <div style={{ ...s.card, marginTop: 12 }}>
          <div style={s.cardTitle}>Plan Status</div>
          <div style={{ marginBottom: 10 }}>
            <span style={s.statusBadge(cov.applicationStatus || '')}>{fmt(cov.applicationStatus) !== '—' ? cov.applicationStatus : 'On File'}</span>
          </div>
          <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>ℹ️ Status is agent-updated. For carrier-confirmed status, contact your agent.</p>
        </div>

        {/* Plan Details */}
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ ...s.cardTitle, marginBottom: 0 }}>Plan Details</span>
            {getIssuerLogo(cov.carrierName) && (
              <img
                src={getIssuerLogo(cov.carrierName)}
                alt={cov.carrierName}
                style={{ maxHeight: 36, maxWidth: '45%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
              />
            )}
          </div>
          {[
            ['Insurance Type', (ins.insuranceType || '').toUpperCase()],
            ['Carrier',        cov.carrierName],
            ['Plan Name',      cov.planName],
            ['Plan Type',      cov.planType],
            ['Member ID',      cov.memberId],
            ['Effective Date', fmtDate(cov.effectiveDate)],
            ['Monthly Premium', fmtCurrency(cov.premium)],
            ['Gross Premium',  fmtCurrency(cov.grossPremium)],
            ['Deductible',     cov.deductible ? `$${cov.deductible}` : null],
            ['Max Out-of-Pocket', cov.maxOop ? `$${cov.maxOop}` : null],
          ].map(([label, val], i, arr) => (
            <div key={label} style={{ ...s.row, ...(i === arr.length - 1 ? { borderBottom: 'none' } : {}) }}>
              <span style={s.rowLabel}>{label}</span>
              <span style={s.rowValue}>{fmt(val)}</span>
            </div>
          ))}
        </div>

        {/* Links */}
        {(cov.benefitsUrl || cov.brochureUrl) && (
          <div style={s.card}>
            <div style={s.cardTitle}>Documents</div>
            {cov.benefitsUrl && (
              <a href={cov.benefitsUrl} target="_blank" rel="noreferrer"
                style={{ display: 'block', padding: '10px 0', fontSize: 14, color: C.brand, fontWeight: 600, borderBottom: `1px solid ${C.light}` }}>
                📄 Summary of Benefits
              </a>
            )}
            {cov.brochureUrl && (
              <a href={cov.brochureUrl} target="_blank" rel="noreferrer"
                style={{ display: 'block', padding: '10px 0', fontSize: 14, color: C.brand, fontWeight: 600 }}>
                📘 Plan Brochure
              </a>
            )}
          </div>
        )}

        {/* Agent Notes */}
        {cov.agentNotes && (
          <div style={s.card}>
            <div style={s.cardTitle}>Agent Notes</div>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, margin: 0 }}>{cov.agentNotes}</p>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════ SECTION: Upload Docs ═══════════ */
  const SectionDocs = () => {
    if (!activeConvId) return (
      <div style={s.section}>
        <div style={s.emptyState}>
          <span style={s.emptyIcon}>📎</span>
          <p style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No active conversation yet</p>
          <p style={{ fontSize: 13 }}>Your agent will set up your channel shortly. Please check back soon.</p>
        </div>
      </div>
    );
    return (
    <div style={s.section}>
      <div style={s.card}>
        <div style={s.cardTitle}>Upload Document</div>
        <p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
          Your agent will be notified when you upload a document.
        </p>

        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.text }}>
          Document Type
        </label>
        <select value={docType} onChange={e => setDocType(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, marginBottom: 14, background: '#fff' }}>
          {DOC_TYPES.map(d => <option key={d}>{d}</option>)}
        </select>

        <div style={s.fileDropZone} onClick={() => fileRef.current?.click()}>
          {uploading ? (
            <p style={{ color: C.brand, fontWeight: 600 }}>Uploading…</p>
          ) : (
            <>
              <span style={{ fontSize: 32, display: 'block', marginBottom: 8 }}>📁</span>
              <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>Tap to select a file</p>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>PDF, JPG, PNG, DOCX — up to 25MB</p>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) uploadDoc(e.target.files[0]); e.target.value = ''; }} />
      </div>

      {/* Recent uploads from chat messages */}
      {messages.filter(m => m.fileUrl).length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>Recent Uploads</div>
          {messages.filter(m => m.fileUrl).map(m => (
            <div key={m.messageId} style={{ ...s.row }}>
              <span style={s.rowLabel}>{m.fileName || 'File'}</span>
              <a href={fileViewUrl(m.fileUrl)} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: C.brand }}>View</a>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  /* ═══════════ SECTION: Requests ═══════════ */
  const SectionRequests = () => (
    <div style={s.section}>
      {!reqForm.show ? (
        <>
          <button style={{ ...s.btnPrimary, marginBottom: 16 }} onClick={() => setReqForm(p => ({ ...p, show: true }))}>
            + New Request
          </button>
          {loading.requests && <div style={s.spinner}>Loading…</div>}
          {!loading.requests && requests.length === 0 && (
            <div style={s.emptyState}>
              <span style={s.emptyIcon}>📋</span>
              <p>No requests yet. Tap above to submit one.</p>
            </div>
          )}
          {requests.map(r => (
            <div key={r.requestId} style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                  {REQUEST_TYPES.find(t => t.id === r.type)?.label || r.type}
                </span>
                <span style={s.badge(r.status === 'open' ? C.warn : C.success)}>{r.status}</span>
              </div>
              {r.note && <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 6px' }}>{r.note}</p>}
              <span style={{ fontSize: 11, color: C.muted }}>{fmtDate(r.createdAt)}</span>
            </div>
          ))}
        </>
      ) : (
        <div style={s.card}>
          <div style={s.cardTitle}>New Request</div>
          {REQUEST_TYPES.map(t => (
            <label key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
              borderBottom: `1px solid ${C.light}`, cursor: 'pointer',
            }}>
              <input type="radio" name="reqType" value={t.id} checked={reqForm.type === t.id}
                onChange={() => setReqForm(p => ({ ...p, type: t.id }))} />
              <span style={{ fontSize: 14 }}>{t.label}</span>
            </label>
          ))}
          <textarea
            placeholder="Additional notes (optional)"
            value={reqForm.note}
            onChange={e => setReqForm(p => ({ ...p, note: e.target.value }))}
            rows={3}
            style={{ width: '100%', marginTop: 12, padding: '9px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={s.actionRow}>
            <button style={s.btnDanger} onClick={() => setReqForm({ type: '', note: '', show: false })}>Cancel</button>
            <button style={{ ...s.btnPrimary, flex: 1, marginTop: 0, marginLeft: 6 }} onClick={submitRequest}>Submit</button>
          </div>
        </div>
      )}
    </div>
  );

  /* ═══════════ SECTION: Updates ═══════════ */
  const SectionUpdates = () => (
    <div style={s.section}>
      {loading.updates && <div style={s.spinner}>Loading…</div>}
      {!loading.updates && updates.length === 0 && (
        <div style={s.emptyState}>
          <span style={s.emptyIcon}>🔔</span>
          <p>No updates yet. Your agent will send updates here.</p>
        </div>
      )}
      {updates.map(u => (
        <div key={u.updateId} style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text, flex: 1 }}>{u.title}</span>
            {!u.readAt && <span style={s.badge(C.brand)}>New</span>}
          </div>
          <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, margin: '0 0 8px' }}>{u.body}</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.muted }}>{u.agentName || 'Your Agent'}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{fmtDate(u.createdAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  /* ═══════════ CHAT OVERLAY ═══════════ */
  const ChatOverlay = () => (
    <div style={{
      position: 'fixed', inset: 0, background: C.bg, zIndex: 300,
      maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column',
    }}>
      {/* header */}
      <div style={{ ...s.header, gap: 12 }}>
        <button onClick={() => setChatOpen(false)}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', padding: '0 6px' }}>←</button>
        <div>
          <div style={s.headerTitle}>Chat with {cov?.agentName || 'Agent'}</div>
          <div style={s.headerSub}>{(cov?.insuranceType || '').toUpperCase()}</div>
        </div>
      </div>
      {/* messages */}
      <div style={s.msgList}>
        {loading.messages && <div style={s.spinner}>Loading messages…</div>}
        {messages.map(m => {
          const mine = m.senderRole === 'lead';
          const viewUrl = m.fileUrl ? fileViewUrl(m.fileUrl) : null;
          return (
            <div key={m.messageId} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              {viewUrl ? (
                <div style={s.msgBubble(mine)}>
                  {m.type === 'image' ? (
                    <>
                      {/* Image preview via proxy (302→presigned) */}
                      <a href={viewUrl} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: 4 }}>
                        <img src={viewUrl} alt={m.fileName || 'Image'} style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
                          onError={e => { e.currentTarget.style.display = 'none'; }} />
                      </a>
                      <a href={viewUrl} target="_blank" rel="noreferrer"
                        style={{ display: 'block', fontSize: 12, color: mine ? 'rgba(255,255,255,.8)' : C.brand, textDecoration: 'none' }}>
                        🖼️ {m.fileName || 'View Image'}
                      </a>
                    </>
                  ) : m.type === 'video' ? (
                    <a href={viewUrl} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: mine ? 'rgba(255,255,255,.15)' : C.light, borderRadius: 8, textDecoration: 'none', color: mine ? '#fff' : C.text }}>
                      <span style={{ fontSize: 20 }}>🎬</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{m.fileName || 'Video'}</span>
                    </a>
                  ) : m.type === 'audio' ? (
                    <a href={viewUrl} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: mine ? 'rgba(255,255,255,.15)' : C.light, borderRadius: 8, textDecoration: 'none', color: mine ? '#fff' : C.text }}>
                      <span style={{ fontSize: 20 }}>🎵</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{m.fileName || 'Audio'}</span>
                    </a>
                  ) : (
                    /* Generic file (PDF, DOC, etc.) */
                    <a href={viewUrl} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: mine ? 'rgba(255,255,255,.15)' : C.light, borderRadius: 8, textDecoration: 'none', color: mine ? '#fff' : C.text }}>
                      <span style={{ fontSize: 24 }}>
                        {(m.mimeType || '').includes('pdf') ? '📄' : '📎'}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{m.fileName || 'File'}</div>
                        {m.fileSize && <div style={{ fontSize: 10, opacity: .7 }}>{(m.fileSize / 1024).toFixed(1)} KB</div>}
                      </div>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, opacity: .7 }}>↓</span>
                    </a>
                  )}
                  {safeContent(m.content) && <p style={{ margin: '6px 0 0', fontSize: 13 }}>{safeContent(m.content)}</p>}
                </div>
              ) : (
                <div style={s.msgBubble(mine)}>{safeContent(m.content)}</div>
              )}
              <span style={s.msgMeta(mine)}>
                {m.channel !== 'chat' ? `[${m.channel?.toUpperCase()}] ` : ''}{fmtDate(m.timestamp)}
              </span>
            </div>
          );
        })}
        <div ref={msgEndRef} />
      </div>
      {/* input */}
      <div style={s.chatInput}>
        <button onClick={() => fileRef.current?.click()}
          style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: '0 4px' }} title="Attach file">📎</button>
        <input
          style={s.textInput}
          placeholder="Type a message…"
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg()}
        />
        <button style={s.sendBtn} onClick={sendMsg}>Send</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx,video/*,audio/*"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) uploadDoc(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );

  /* ── unread updates count ── */
  const unreadCount = updates.filter(u => !u.readAt).length;

  /* ═══════════ SECTION: Benefits ═══════════ */
  const SectionBenefits = () => {
    const agentIns = coverages[activeIns] || coverages[0] || {};
    const openLink = (url) => {
      if (!url) return;
      if (window.Capacitor?.isNativePlatform?.()) {
        import('@capacitor/browser').then(({ Browser }) => { Browser.open({ url }); }).catch(() => { window.open(url, '_blank'); });
      } else { window.open(url, '_blank'); }
    };
    const optIns = [
      { id: 'dental', label: 'Dental', icon: '🦷', bg: '#d1fae5', link: agentIns.dentalInsuranceLink },
      { id: 'life',   label: 'Life Insurance', icon: '❤️', bg: '#fee2e2', link: agentIns.lifeInsuranceLink },
      { id: 'auto',   label: 'Auto Insurance', icon: '🚗', bg: '#ede9fe', link: agentIns.autoInsuranceLink },
    ].filter(x => x.link);
    const partners = [
      { id: 'debt',   label: 'Debt Relief', icon: '📄', bg: '#fef9c3', link: agentIns.debtReliefLink },
      { id: 'credit', label: 'Credit Services', icon: '📊', bg: '#cffafe', link: agentIns.creditServicesLink },
      { id: 'mca',    label: 'MCA Services', desc: 'For business owners', icon: '💼', bg: '#ede9fe', link: agentIns.mcaLink },
    ].filter(x => x.link);
    const linkRow = (item, cta) => (
      <div key={item.id} onClick={() => openLink(item.link)}
        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderTop: `1px solid ${C.light}`, cursor: 'pointer', background: '#fff' }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{item.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{item.label}</div>
          {item.desc && <div style={{ fontSize: 12, color: C.muted }}>{item.desc}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.brand }}>{cta}</span>
          <span style={{ color: C.brand, fontSize: 14 }}>↗</span>
        </div>
      </div>
    );
    return (
      <div style={s.section}>
        <div style={{ ...s.card, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 4px' }}>Coverage &amp; Services</p>
            <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5 }}>Plan benefits, optional coverage and partner services.</p>
          </div>
          <img src={`${process.env.PUBLIC_URL}/AppLogoShort.png`} alt="" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 12, flexShrink: 0, marginLeft: 12 }} />
        </div>
        {optIns.length > 0 && (
          <div style={{ ...s.card, marginBottom: 14, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 10px' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 2 }}>Optional Insurance</div>
              <div style={{ fontSize: 12, color: C.muted }}>Separate coverage. Terms and eligibility apply.</div>
            </div>
            {optIns.map(item => linkRow(item, 'Sign Up'))}
          </div>
        )}
        {partners.length > 0 && (
          <div style={{ ...s.card, marginBottom: 14, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 10px' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 2 }}>Optional Partner Services</div>
              <div style={{ fontSize: 12, color: C.muted }}>Third-party services. Not insurance benefits.</div>
            </div>
            {partners.map(item => linkRow(item, 'Visit Partner'))}
          </div>
        )}
        <div style={{ ...s.card, marginBottom: 4, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Your Plan Benefits</div>
          </div>
          {BENEFIT_ITEMS.map((b, i) => (
            <div key={b.id} onClick={() => { setBenefitDetail(b); setBenefitNote(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderTop: `1px solid ${C.light}`, cursor: 'pointer', background: '#fff' }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, background: b.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: `0 2px 8px ${b.bg}55` }}>{b.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2 }}>{b.label}</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>{b.desc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.brand }}>Request</span>
                <span style={{ color: C.brand, fontSize: 15, fontWeight: 700 }}>›</span>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
          Optional offers are provided by third parties and are separate from your insurance plan. Terms and eligibility apply.
        </p>
      </div>
    );
  };

  /* ═══════════ BENEFIT DETAIL OVERLAY ═══════════ */
  const BenefitDetailOverlay = () => (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 300, maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...s.header, gap: 12 }}>
        <button onClick={() => setBenefitDetail(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', padding: '0 6px' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={s.headerTitle}>{benefitDetail.label}</div>
          <div style={s.headerSub}>Benefit Request</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div style={{ textAlign: 'center', padding: '24px 0 20px' }}>
          <div style={{ width: 80, height: 80, borderRadius: 20, background: benefitDetail.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, boxShadow: `0 4px 20px ${benefitDetail.bg}66`, marginBottom: 14 }}>{benefitDetail.icon}</div>
          <p style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>{benefitDetail.label}</p>
          <p style={{ fontSize: 14, color: C.muted, margin: 0, lineHeight: 1.5 }}>{benefitDetail.desc}</p>
        </div>
        <div style={s.card}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>Send a {benefitDetail.label} Request</p>
          <p style={{ fontSize: 13, color: C.muted, margin: '0 0 12px', lineHeight: 1.5 }}>Your agent will follow up with details about your {benefitDetail.label.toLowerCase()} benefit.</p>
          <textarea
            placeholder="Add a note for your agent (optional)"
            value={benefitNote}
            onChange={e => setBenefitNote(e.target.value)}
            rows={4}
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }}
          />
          <div style={{ ...s.actionRow, marginTop: 12 }}>
            <button style={s.btnDanger} onClick={() => setBenefitDetail(null)}>Cancel</button>
            <button style={{ ...s.btnPrimary, flex: 1, marginTop: 0, marginLeft: 6 }} onClick={submitBenefitRequest}>Send Request</button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ═══════════ UPDATES OVERLAY (bell icon) ═══════════ */
  const UpdatesOverlay = () => (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 300, maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...s.header, gap: 12 }}>
        <button onClick={() => setUpdatesOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', padding: '0 6px' }}>←</button>
        <div style={s.headerTitle}>Updates</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={s.section}>
          {loading.updates && <div style={s.spinner}>Loading…</div>}
          {!loading.updates && updates.length === 0 && (
            <div style={s.emptyState}><span style={s.emptyIcon}>🔔</span><p>No updates yet. Your agent will send updates here.</p></div>
          )}
          {updates.map(u => (
            <div key={u.updateId} style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text, flex: 1 }}>{u.title}</span>
                {!u.readAt && <span style={s.badge(C.brand)}>New</span>}
              </div>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, margin: '0 0 8px' }}>{u.body}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.muted }}>{u.agentName || 'Your Agent'}</span>
                <span style={{ fontSize: 11, color: C.muted }}>{fmtDate(u.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ─── greeting + first name ─── */
  const displayName = profile?.name || '';
  const firstName   = displayName.split(' ')[0] || 'there';
  const _hr         = new Date().getHours();
  const greeting    = _hr < 12 ? 'Good morning' : _hr < 17 ? 'Good afternoon' : 'Good evening';

  /* ─── render ─── */
  return (
    <div style={s.root}>
      {/* Header — greeting style matching reference */}
      <div style={s.header}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
            {greeting}, {firstName} 👋
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.78)', marginTop: 3 }}>
            We're here to make healthcare simpler for you.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Play Store sticky button — Android web only, shown after banner dismissed */}
          {isAndroidWeb && !showAndroidAppModal && (
            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" title="Download Our App"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <div style={{ background: 'rgba(255,255,255,.22)', border: '1.5px solid rgba(255,255,255,.55)', borderRadius: 8, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l13 8.5-13 8.5c-.5.33-1.5.33-1.5-.5z"/></svg>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>Get App</span>
              </div>
            </a>
          )}
          {/* App Store sticky button — iOS Safari only, shown after banner dismissed */}
          {isIosWeb && !showIosAppModal && (
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" title="Download on App Store"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <div style={{ background: 'rgba(255,255,255,.22)', border: '1.5px solid rgba(255,255,255,.55)', borderRadius: 8, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>Get App</span>
              </div>
            </a>
          )}
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setUpdatesOpen(true)}>
            <span style={{ fontSize: 22 }}>🔔</span>
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, background: C.danger, color: '#fff', borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, padding: '0 4px' }}>{unreadCount}</span>
            )}
          </div>
          {/* Avatar circle → dropdown with SVG icons */}
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setAvatarDropdown(p => !p)}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(255,255,255,.22)',
                border: '2px solid rgba(255,255,255,.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 15, fontWeight: 800, color: '#fff',
                userSelect: 'none', flexShrink: 0,
              }}
            >
              {firstName.charAt(0).toUpperCase()}
            </div>
            {avatarDropdown && (
              <>
                <div onClick={() => setAvatarDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 490 }} />
                <div style={{
                  position: 'absolute', top: 44, right: 0,
                  background: '#fff', borderRadius: 12,
                  boxShadow: '0 4px 24px rgba(0,0,0,.18)',
                  minWidth: 164, zIndex: 500, overflow: 'hidden',
                }}>
                  <div
                    onClick={() => { setAvatarDropdown(false); setProfileOpen(true); }}
                    style={{ padding: '13px 16px', fontSize: 14, color: C.text, cursor: 'pointer', borderBottom: `1px solid ${C.border}`, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    My Account
                  </div>
                  <div
                    onClick={() => { setAvatarDropdown(false); handleLogout(); }}
                    style={{ padding: '13px 16px', fontSize: 14, color: C.danger, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    Logout
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div>
        {tab === 'agent'    && SectionAgent()}
        {tab === 'coverage' && SectionCoverage()}
        {tab === 'docs'     && SectionDocs()}
        {tab === 'benefits' && SectionBenefits()}
        {tab === 'requests' && SectionRequests()}
      </div>

      {/* Bottom Nav */}
      <nav style={s.navBar}>
        {NAV.map(n => (
          <div key={n.id} style={s.navItem(tab === n.id)} onClick={() => setTab(n.id)}>
            <span style={s.navIcon}>{n.icon}</span>
            <span style={s.navLabel(tab === n.id)}>{n.label}</span>
          </div>
        ))}
      </nav>

      {/* Chat overlay */}
      {chatOpen && ChatOverlay()}

      {/* Benefit detail overlay */}
      {benefitDetail && BenefitDetailOverlay()}

      {/* Updates overlay (bell) */}
      {updatesOpen && UpdatesOverlay()}

      {/* My Account overlay */}
      {profileOpen && (
        <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 300, maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...s.header, gap: 12 }}>
            <button onClick={() => setProfileOpen(false)}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', padding: '0 6px' }}>←</button>
            <div style={s.headerTitle}>My Account</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24, paddingTop: 16 }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: C.brand + '22',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, fontWeight: 800, color: C.brand,
                marginBottom: 12, border: `2px solid ${C.brand}33`,
              }}>
                {firstName.charAt(0).toUpperCase()}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{ghlProfile?.firstName ? `${ghlProfile.firstName} ${ghlProfile.lastName || ''}`.trim() : (profile?.name || 'My Account')}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{ghlProfile?.email || profile?.leadEmail || profile?.email || ''}</div>
            </div>
            <div style={s.card}>
              <div style={s.cardTitle}>Account Info</div>
              {loadingGhlProfile
                ? <div style={{ fontSize: 13, color: C.muted, padding: '8px 0', textAlign: 'center' }}>Loading…</div>
                : [
                  ['First Name',    ghlProfile?.firstName || (profile?.name || '').split(' ')[0]],
                  ['Last Name',     ghlProfile?.lastName  || (profile?.name || '').split(' ').slice(1).join(' ')],
                  ['Email',         ghlProfile?.email     || profile?.leadEmail || profile?.email],
                  ['Phone',         ghlProfile?.phone     || profile?.phone],
                  ['Address',       ghlProfile?.address],
                  ['City',          ghlProfile?.city],
                  ['State',         ghlProfile?.state],
                  ['Postal Code',   ghlProfile?.postalCode],
                  ['County',        ghlProfile?.county],
                  ['Date of Birth', ghlProfile?.dateOfBirth],
                ].filter(([, val]) => val && val !== '').map(([label, val]) => (
                  <div key={label} style={s.row}>
                    <span style={s.rowLabel}>{label}</span>
                    <span style={s.rowValue}>{fmt(val)}</span>
                  </div>
                ))
              }
            </div>
            <button
              onClick={() => { setProfileOpen(false); handleLogout(); }}
              style={{ ...s.btnPrimary, background: C.danger, marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Logout
            </button>
          </div>
        </div>
      )}

      {/* ── Android App Download Modal — "You're signed in!" ── */}
      {isAndroidWeb && showAndroidAppModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 600, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '28px 20px 36px', width: '100%', maxWidth: 480, boxShadow: '0 -4px 32px rgba(0,0,0,.18)', textAlign: 'center' }}>
            {/* Check icon */}
            <div style={{ width: 66, height: 66, borderRadius: '50%', background: '#e6f9f0', border: '3px solid #0e9f6e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 34 }}>✅</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>You're signed in!</h2>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 22px', lineHeight: 1.55 }}>
              Download our <strong>free app</strong> for push notifications, instant agent updates, and a better mobile experience.
            </p>

            {/* Credentials — use signupMethod to pick the right identifier */}
            <div style={{ background: '#f3f4f6', borderRadius: 12, padding: '14px 16px', marginBottom: 22, textAlign: 'left' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Use these to log in to the app:
              </p>

              {/* Email — shown for email-signup or agent-created (non-phone) leads */}
              {profile?.signupMethod !== 'phone' && (profile?.leadEmail || profile?.email) && !(profile?.leadEmail || '').includes('@lead.') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>📧</span>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Email (username)</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{profile.leadEmail || profile.email}</div>
                  </div>
                </div>
              )}

              {/* Phone — always shown if available; primary for phone-only signups */}
              {profile?.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>📱</span>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{profile?.signupMethod === 'phone' ? 'Phone (username)' : 'Phone'}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{profile.phone}</div>
                  </div>
                </div>
              )}

              {/* Password hint */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
                <span style={{ fontSize: 18 }}>🔑</span>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Password</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                    {profile?.signupMethod === 'email' || profile?.signupMethod === 'phone'
                      ? 'The password you created when you signed up'
                      : 'Check the SMS & email we sent you — your temporary password is there'}
                  </div>
                </div>
              </div>
            </div>

            {/* Open App button:
                Android → real <a href="intent://..."> tap. Chrome intercepts intent:// only
                from a REAL link click (not JS navigation) — opens app if installed, or Play Store
                via S.browser_fallback_url if not installed.
                iOS / Desktop → button that calls dismissAndroidModal() */}
            {isAndroidWeb ? (
              <a
                href={ANDROID_INTENT_URL}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  background: '#1a56db', color: '#fff', textDecoration: 'none', borderRadius: 12,
                  padding: '14px 20px', fontWeight: 700, fontSize: 16, marginBottom: 14,
                  boxShadow: '0 4px 14px rgba(26,86,219,.35)', width: '100%', boxSizing: 'border-box',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                  <path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l13 8.5-13 8.5c-.5.33-1.5.33-1.5-.5z"/>
                </svg>
                Open App / Download
              </a>
            ) : (
              <button
                onClick={dismissAndroidModal}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  background: '#1a56db', color: '#fff', border: 'none', borderRadius: 12,
                  padding: '14px 20px', fontWeight: 700, fontSize: 16, marginBottom: 14,
                  boxShadow: '0 4px 14px rgba(26,86,219,.35)', cursor: 'pointer', width: '100%',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                  <path d="M3 20.5v-17c0-.83 1-.83 1.5-.5l13 8.5-13 8.5c-.5.33-1.5.33-1.5-.5z"/>
                </svg>
                Continue
              </button>
            )}

            {/* Dismiss */}
            <button
              onClick={dismissAndroidModal}
              style={{ background: 'transparent', border: 'none', fontSize: 14, color: '#9ca3af', cursor: 'pointer', padding: '8px 16px', width: '100%' }}
            >
              Continue in browser →
            </button>
          </div>
        </div>
      )}

      {/* ── iOS App Store Modal ── */}
      {isIosWeb && showIosAppModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 600, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '28px 20px 36px', width: '100%', maxWidth: 480, boxShadow: '0 -4px 32px rgba(0,0,0,.18)', textAlign: 'center' }}>
            {/* App icon */}
            <div style={{ width: 66, height: 66, borderRadius: '50%', background: '#e8f0fe', border: '3px solid #1a56db', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 34 }}>📱</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>You're signed in!</h2>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 22px', lineHeight: 1.55 }}>
              Download our <strong>free app</strong> for push notifications, instant agent updates, and a better mobile experience.
            </p>

            {/* Credentials */}
            <div style={{ background: '#f3f4f6', borderRadius: 12, padding: '14px 16px', marginBottom: 22, textAlign: 'left' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Use these to log in to the app:
              </p>
              {profile?.signupMethod !== 'phone' && (profile?.leadEmail || profile?.email) && !(profile?.leadEmail || '').includes('@lead.') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>📧</span>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Email (username)</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{profile.leadEmail || profile.email}</div>
                  </div>
                </div>
              )}
              {profile?.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>📱</span>
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{profile?.signupMethod === 'phone' ? 'Phone (username)' : 'Phone'}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{profile.phone}</div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
                <span style={{ fontSize: 18 }}>🔑</span>
                <div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Password</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                    {profile?.signupMethod === 'email' || profile?.signupMethod === 'phone'
                      ? 'The password you created when you signed up'
                      : 'Check the SMS & email we sent you — your temporary password is there'}
                  </div>
                </div>
              </div>
            </div>

            {/* App Store button */}
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={dismissIosModal}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#1a56db', color: '#fff', textDecoration: 'none', borderRadius: 12,
                padding: '14px 20px', fontWeight: 700, fontSize: 16, marginBottom: 14,
                boxShadow: '0 4px 14px rgba(26,86,219,.35)', width: '100%', boxSizing: 'border-box',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on the App Store
            </a>

            {/* Dismiss */}
            <button
              onClick={dismissIosModal}
              style={{ background: 'transparent', border: 'none', fontSize: 14, color: '#9ca3af', cursor: 'pointer', padding: '8px 16px', width: '100%' }}
            >
              Continue in browser →
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      <div style={s.toast(showToast)}>{toastMsg}</div>
    </div>
  );
}
